/**
 * PianoKeys Component
 * Realistic, proportional piano keyboard reference for the Piano Roll editor
 * Styled with authentic ivory & ebony 3D key caps, octave markers & active key press feedback.
 */

import React, { useRef } from 'react';
import { AudioEngine } from '../core/audioEngine';
import { getPitchName, isBlackKey } from '../core/timing';

interface PianoKeysProps {
  minPitch: number;
  maxPitch: number;
  pitchRowHeight: number;
  audioEngine: AudioEngine;
  activePitches?: Set<number>;
  width?: number; // default 80px
}

export const PianoKeys: React.FC<PianoKeysProps> = ({
  minPitch,
  maxPitch,
  pitchRowHeight,
  audioEngine,
  activePitches = new Set(),
  width = 80,
}) => {
  const activeReleaseRef = useRef<((time?: number) => void) | null>(null);

  const pitches: number[] = [];
  for (let p = maxPitch; p >= minPitch; p--) {
    pitches.push(p);
  }

  const handleMouseDown = (e: React.MouseEvent, pitch: number) => {
    if (e.button !== 0) return;
    if (activeReleaseRef.current) {
      activeReleaseRef.current();
    }
    const release = audioEngine.playNoteOn(pitch, 100);
    activeReleaseRef.current = release;

    const handleMouseUp = () => {
      if (activeReleaseRef.current) {
        activeReleaseRef.current();
        activeReleaseRef.current = null;
      }
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mouseup', handleMouseUp);
  };

  const totalHeight = (maxPitch - minPitch + 1) * pitchRowHeight;

  return (
    <div
      id="piano-keys-sidebar"
      style={{ width: `${width}px`, height: `${totalHeight}px` }}
      className="relative flex-shrink-0 bg-[#FFFFFF] border-r-2 border-[#1B1B1B] select-none z-10 shadow-xs"
    >
      {pitches.map((pitch) => {
        const black = isBlackKey(pitch);
        const isC = pitch % 12 === 0;
        const isMiddleC = pitch === 60;
        const isActive = activePitches.has(pitch);
        const name = getPitchName(pitch);
        const y = (maxPitch - pitch) * pitchRowHeight;

        // Natural semitone boundary dividers on piano keyboard:
        // Between B & C and between E & F
        const isNaturalBoundary = isC || pitch % 12 === 5; // C or F

        if (black) {
          // Proportional Raised Ebony Key
          return (
            <div
              key={pitch}
              id={`piano-key-${pitch}`}
              onMouseDown={(e) => handleMouseDown(e, pitch)}
              style={{
                top: `${y}px`,
                height: `${pitchRowHeight}px`,
              }}
              className="absolute left-0 right-0 flex items-center justify-end pr-0.5 cursor-pointer z-10 group overflow-hidden"
              title={`${name} (MIDI ${pitch})`}
            >
              {/* Background underlying white key bed matching grid stripe */}
              <div
                className={`absolute inset-0 bg-[#F2F0EB] pointer-events-none border-b ${
                  isNaturalBoundary ? 'border-b-[#C4C1B7]' : 'border-b-[#E8E6E0]'
                }`}
              />

              {/* Elevated Ebony Cap: proportional 65% width, 3D beveled appearance */}
              <div
                style={{
                  width: `${Math.round(width * 0.65)}px`,
                  height: `${pitchRowHeight - 1}px`,
                }}
                className={`relative flex items-center justify-between px-1.5 rounded-r-xs border border-neutral-900 transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 text-neutral-950 shadow-[0_0_10px_rgba(245,158,11,0.8)]'
                    : 'bg-gradient-to-r from-neutral-900 via-neutral-800 to-neutral-950 text-neutral-400 group-hover:from-neutral-800 group-hover:to-neutral-900 shadow-[0_2px_4px_rgba(0,0,0,0.85),-2px_0_3px_rgba(0,0,0,0.6)]'
                }`}
              >
                {/* Top glossy specular highlight */}
                <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-white/20 rounded-t-xs pointer-events-none" />

                {/* Right edge beveled lip */}
                <div className="absolute top-0 right-0 bottom-0 w-[2px] bg-neutral-700/60 rounded-r-xs pointer-events-none" />

                <span className="text-[8px] font-mono font-medium truncate pointer-events-none opacity-80 group-hover:opacity-100 leading-none">
                  {name.replace('#', '♯')}
                </span>

                <div
                  className={`w-1.5 h-1.5 rounded-full ${
                    isActive ? 'bg-neutral-950' : 'bg-transparent'
                  }`}
                />
              </div>
            </div>
          );
        }

        // Proportional Ivory White Key
        return (
          <div
            key={pitch}
            id={`piano-key-${pitch}`}
            onMouseDown={(e) => handleMouseDown(e, pitch)}
            style={{
              top: `${y}px`,
              height: `${pitchRowHeight}px`,
            }}
            className={`absolute left-0 right-0 flex items-center justify-between px-2 text-[9px] font-mono cursor-pointer transition-all border-b overflow-hidden ${
              isNaturalBoundary ? 'border-b-[#C4C1B7]' : 'border-b-[#E8E6E0]'
            } ${
              isActive
                ? 'bg-gradient-to-r from-amber-300 via-amber-200 to-amber-100 text-amber-950 shadow-inner font-bold'
                : isC
                ? 'bg-[#EEF2FF] text-neutral-900 hover:brightness-95'
                : 'bg-[#FFFFFF] text-neutral-800 hover:bg-[#F8F7F4]'
            }`}
            title={`${name} (MIDI ${pitch})`}
          >
            {/* Subtle ivory key grain & right edge shadow */}
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-r from-transparent to-neutral-400/20 pointer-events-none" />

            {/* Note Name & Octave Badge */}
            <div className="flex items-center gap-1 z-0 leading-none">
              {isC ? (
                <span
                  className={`px-1.5 py-0.5 rounded-xs text-[8px] font-bold shadow-xs leading-none ${
                    isMiddleC
                      ? 'bg-blue-600 text-white shadow-blue-900/40 ring-1 ring-blue-400'
                      : 'bg-neutral-800 text-white'
                  }`}
                >
                  {name}
                  {isMiddleC && <span className="ml-1 text-[7px] font-normal opacity-90">Mid</span>}
                </span>
              ) : (
                <span className="font-semibold text-neutral-600 text-[9px] leading-none">{name}</span>
              )}
            </div>

            {/* Active key indicator bead */}
            <span
              className={`w-1.5 h-1.5 rounded-full transition-transform ${
                isActive ? 'bg-amber-600 scale-125' : 'bg-transparent'
              }`}
            />
          </div>
        );
      })}
    </div>
  );
};
